<template>
    <div id="create" class="flex">
        <div class="ff author-pane flex flex-column">
            <div class="header flex flex-align">
                <div class="wordmark"></div>
                <a class="header-option new" v-on:click="restart()">New</a>
                <a class="header-option help" href="https://katex.org/docs/supported.html" target="_blank">Help</a>
                <div class="ff"></div>
                <div class="header-title">Latex Code</div>
            </div>
            <div class="ff editor-container">
                <div class="published-ribbon center" v-if="formula.published">
                    This formula is published and can no longer be edited. Create a new formula, or fork this formula to make changes.
                </div>
                <editor :content.sync="formula.data.latex" v-on:change="update()" :disabled="formula.published"></editor>
            </div>
            <div class="footer flex flex-align"></div>
        </div>
        <div class="ff preview-pane flex flex-column" :style="style">
            <div class="header flex flex-align">
                <a class="header-option code" v-on:click="confirm()" :class="{disabled: formula.data.latex.length === 0}" v-if="!formula.published">Publish</a>
                <a class="header-option share" v-on:click="share()" v-if="formula.published">Share</a>
                <a class="header-option fork" v-on:click="fork()">Fork</a>
                <div class="ff"></div>
                <div class="header-title">Formula Preview</div>
            </div>
            <div class="ff preview-container flex flex-align flex-center">
                <katex-element :expression="formula.data.latex" :display-mode="true" error-color="#EA2027" ref="katex" />
            </div>
        </div>
        <div class="modal-backdrop flex flex-align flex-center" v-if="modal">
            <div class="modal confirm" v-if="modal === 'confirm'">
                <div class="modal-message">Are you sure you want to publish this formula? You will have to fork to make any additional changes.</div>
                <div class="flex">
                    <a class="ff modal-option modal-primary-option" v-on:click="publish()">Publish</a>
                    <a class="ff modal-option" v-on:click="wait()">Cancel</a>
                </div>
            </div>
            <div class="modal confirm" v-if="modal === 'error'">
                <div class="modal-message">{{error}}</div>
                <div class="flex">
                    <a class="ff modal-option modal-primary-option" v-on:click="restart()">New Formula</a>
                    <a class="ff modal-option" v-on:click="cancel()">Close</a>
                </div>
            </div>
            <div class="modal share" v-if="modal === 'share'">
                <div class="share-options">
                    <div class="share-title">Direct Link</div>
                    <input type="text" class="share-input reset-input" readonly v-model="direct" />
                    <div class="share-title">Embed This Formula</div>
                    <input type="text" class="share-input reset-input" readonly v-model="embed" />
                </div>
                <div class="flex">
                    <a class="ff modal-option modal-primary-option" v-on:click="modal = ''">Done</a>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'create',
    data: function(){
        return {
            formula: {
                published: false,
                data: {
                    latex: ''
                },
                object_uuid: ''
            },
            modal: '',
            error: '',
            debounce: null,
            style: {
                background: '#FFFFFF',
                color: '#000000'
            }
        };
    },
    computed: {
        direct: function(){
            return `https://math.embed.fun/${this.formula.object_uuid}`;
        },
        embed: function(){
            const width = Math.max(this.$refs.katex.$el.clientWidth, 200);
            const height = this.$refs.katex.$el.clientHeight + 80;

            return `<iframe src="https://math.embed.fun/embed/${this.formula.object_uuid}" frameborder="0" width="${width}" height="${height}"></iframe>`;
        }
    },
    methods: {
        cancel: function(){
            this.modal = '';
            this.error = '';
        },
        wait: function(){
            this.modal = '';
        },
        publish: function(){
            if(this.debounce){
                clearTimeout(this.debounce);
            }

            if(!this.formula.published && this.formula.data.latex.length){
                this.$http.post(`/api/v1/formulas/${this.formula.object_uuid}/publish`, this.formula)
                .then(response => response.body)
                .then(() => {
                    this.formula.published = true;
                    this.modal = 'share';
                })
                .catch(() => {
                    this.modal = 'error';
                    this.error = 'There was an error publishing your formula.';
                });
            }
        },
        share: function(){
            if(this.formula.published){
                this.modal = 'share';
            }
        },
        confirm: function(){
            if(!this.formula.published && this.formula.data.latex.length){
                this.modal = 'confirm';
            }
        },
        fork: function(){
            this.$http.post(`/api/v1/formulas/${this.formula.object_uuid}/fork`)
            .then(response => response.body)
            .then(formula => {
                this.formula = formula;
                this.$router.push({name: 'formula', params: {uuid: this.formula.object_uuid}});
            })
            .catch(function(){
                this.modal = 'error';
                this.error = 'There was an error creating your fork.';
            });
        },
        restart: function(){
            this.cancel();

            if(this.debounce){
                clearTimeout(this.debounce);
            }

            this.$http.post(`/api/v1/formulas`, {})
            .then(response => response.body)
            .then(formula => {
                this.formula = formula;
                this.$router.push({name: 'formula', params: {uuid: this.formula.object_uuid}});
            })
            .catch(() => {
                this.modal = 'error';
                this.error = 'There was an error creating your new formula.';
            });
        },
        update: function(){
            if(!this.formula.published){
                const width = Math.max(this.$refs.katex.$el.clientWidth, 200);
                const height = this.$refs.katex.$el.clientHeight + 80;

                this.formula.width = width;
                this.formula.height = height;

                if(this.debounce){
                    clearTimeout(this.debounce);
                }

                this.debounce = setTimeout(() => {
                    this.$http.put(`/api/v1/formulas/${this.formula.object_uuid}`, this.formula);
                }, 3000);
            }
        }
    },
    created: function(){
        const formula_uuid = this.$route.params.uuid;

        if(formula_uuid){
            this.$http.get(`/api/v1/formulas/${formula_uuid}`)
            .then(response => response.body)
            .then(formula => this.formula = formula)
            .catch(() => {
                this.modal = 'error';
                this.error = 'There was an error loading this formula.';
            });
        }else{
            this.restart();
        }
    }
}
</script>

<style scoped lang="less">
    @import "../less/variables";
    @import "../less/mixins";

    #create
    {
        width: 100%;
        height: 100%;
        overflow: hidden;
    }

    .author-pane
    {
        background: @f8;
        border-right: 1px solid #E4E4E4;
    }

    .preview-pane
    {

    }

    .published-ribbon
    {
        font-size: 14px;
        font-weight: 500;
        background: @d;
        color: @darkgrey;
        margin-bottom: 10px;
        padding: 12px;
    }

    .header
    {
        height: 50px;
        padding: 0 20px;

        .header-option
        {
            font-size: 13px;
            text-transform: uppercase;
            color: #000;
            font-weight: 700;
            padding-left: 24px;
            margin-right: 30px;
            letter-spacing: 0.03rem;
            opacity: 0.5;
            transition: opacity ease 0.25s;
            cursor: pointer;
            text-decoration: none;

            &:hover, &:active
            {
                opacity: 1;
            }

            &.disabled
            {
                opacity: 0.3;
                pointer-events: none;
            }
        }

        .header-title
        {
            font-size: 14px;
            font-weight: 500;
            color: #AAAAAA;
        }
    }

    .editor-container
    {
        overflow: auto;
    }

    .wordmark
    {
        background: url('../assets/wordmark.svg') 0 0 no-repeat;
        background-size: 114px auto;
        width: 114px;
        height: 13px;
        margin-right: 20px;
    }

    .header-option.code
    {
        background: url('../assets/code.svg') left center no-repeat;
        background-size: 18px auto;
    }

    .header-option.fork
    {
        background: url('../assets/fork.svg') left center no-repeat;
        background-size: 18px auto;
    }

    .header-option.share
    {
        background: url('../assets/code.svg') left center no-repeat;
        background-size: 18px auto;
    }

    .header-option.help
    {
        background: url('../assets/help.svg') left center no-repeat;
        background-size: 15px auto;
    }

    .header-option.new
    {
        background: url('../assets/create.svg') left center no-repeat;
        background-size: 16px auto;
    }

    .modal-backdrop
    {
        background: rgba(0, 0, 0, 0.5);
        position: fixed;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        z-index: 9999999;
    }

    .modal
    {
        border-radius: 3px;
        background: @f;
        box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
        min-width: 240px;
        min-height: 120px;
        overflow: hidden;
    }

    .modal-message
    {
        color: @grey;
        font-size: 14px;
        width: 300px;
        line-height: 18px;
        padding: 30px;
        text-align: center;
    }

    .modal-option
    {
        border-top: 1px solid @d;
        border-right: 1px solid @d;
        font-size: 16px;
        font-weight: 500;
        color: @grey;
        padding: 14px 0;
        text-align: center;
        cursor: pointer;
        transition: background-color ease 0.25s;

        &:last-child
        {
            border-right: 0;
        }

        &:hover, &:active
        {
            background: @f8;
        }
    }

    .modal-primary-option
    {
        color: @blue;
    }

    .modal.share
    {
        width: 100%;
        max-width: 400px;
    }

    .share-title
    {
        font-weight: 500;
        color: @black;
        margin-bottom: 8px;
    }

    .share-input
    {
        height: 40px;
        background: @f;
        border: 2px solid @d;
        border-radius: 3px;
        color: #555;
        padding: 0 10px;
        width: 100%;
        margin-bottom: 30px;
        font-size: 15px;
        transition: border-color ease 0.25s;
        line-height: 36px;

        &:hover, &:active
        {
            border-color: #AAA;
        }

        &:focus
        {
            border-color: @blue;
        }

        &:last-child
        {
            margin-bottom: 0;
        }
    }

    .share-options
    {
        padding: 30px;
    }
</style>
